import {
  assign,
  enqueueActions,
  fromPromise,
  setup,
  type AnyActorRef,
  type SnapshotFrom,
} from 'xstate';
import { firstValueFrom } from 'rxjs';
import {
  type Reservation,
  type ReservationPhone,
  TripLegType,
} from '@/data/models/reservation.model';
import reservationService from '@/services/reservationService';
import reservationUtils from '@/services/reservationUtils';
import {
  tripLegReservationFormSchema,
  type ReservationFormValues,
} from '../schema/reservationForm.schema';
import { filterReservationErrorsExcludingMemberPhones } from '../utils/filterReservationErrorsExcludingMemberPhones';
import {
  levelOfCareMachine,
  type LevelOfCareContext,
} from './levelOfCare.machine';
import { passengerMobilityMachine } from './passengerMobility.machine';
import { passengerAssistanceMachine } from './passengerAssistance.machine';
import { standingOrderMachine } from './standingOrder.machine';
import { appointmentDateMachine } from './appointmentDate.machine';
import { appointmentDateTimeMachine } from './appointmentDateTime.machine';
import { dropoffAddressMachine } from './dropoffAddress.machine';
import { dropoffNotesMachine } from './dropoffNotes.machine';
import { dropoffPhoneMachine } from './dropoffPhone.machine';
import { pickupAddressMachine } from './pickupAddress.machine';
import { pickupDateMachine } from './pickupDate.machine';
import { pickupDateTimeMachine } from './pickupDateTime.machine';
import { pickupNotesMachine } from './pickupNotes.machine';
import { pickupPhoneMachine } from './pickupPhone.machine';
import { reasonForTripMachine } from './reasonForTrip.machine';
import { tripTypeMachine } from './tripType.machine';
import { willCallMachine } from './willCall.machine';
import {
  hasBlockingTripLegConfidenceError,
  isLowConfidenceError,
  mergeTripLegConfidenceResponsesIntoReservationPayload,
  type TripLegConfidenceStoredEntry,
} from './tripLegConfidence';
import {
  clearTripLegScoreCache,
  runBundledValidation,
  type BundledValidationInput,
  type BundledValidationOutput,
} from './runBundledValidation';
import { appointmentTimeAllowedForPortalMachine } from './appointmentTimeAllowedForPortal.machine';
import { requestOverrideMachine } from './requestOverride.machine';
import {
  tripLegEstimatedPickupMachine,
  type GetDirectionsFn,
} from './tripLegEstimatedPickup.machine';
import type {
  ReservationFormMainContext,
  ReservationFormMainEvent,
  ReservationFormMainFeatureFlags,
  ReservationFormMainInput,
} from './reservationFormMain.types';
import {
  canReservationBeOverriddenFromContext,
  canSaveAsDraftFromContext,
  canSaveAsDraftFromEvent,
  canSaveAsDraftFromFormValues,
  canSubmitFromContext,
  canSubmitFromSubmitEvent,
} from './guards';
import { isFieldEditedWithReservation } from '../utils/isFieldEditedWithReservation';
import { shouldValidateReservation } from '../utils/shouldValidateReservation';

export type {
  ReservationFormMainContext,
  ReservationFormMainEvent,
  ReservationFormMainFeatureFlags,
  ReservationFormMainInput,
} from './reservationFormMain.types';

export { getAggregatedFormValues } from '../utils/getAggregatedFormValues';
export {
  canReservationBeOverriddenFromContext,
  canSaveAsDraftFromContext,
  canSaveAsDraftFromFormValues,
} from './guards';

function shouldValidateAndHasReservation({
  context,
  event,
}: {
  context: ReservationFormMainContext;
  event: ReservationFormMainEvent;
}): boolean {
  return (
    shouldValidateReservation(context, event) &&
    isFieldEditedWithReservation(event)
  );
}

const defaultFeatureFlags: ReservationFormMainFeatureFlags = {
  addressValidationEnabled: false,
  isClientPortal: false,
};

const initialContext: ReservationFormMainContext = {
  tripTypeRef: null,
  reasonForTripRef: null,
  appointmentDateRef: null,
  appointmentDateTimeRef: null,
  pickupDateRef: null,
  pickupDateTimeRef: null,
  pickupTimeZoneRef: null,
  dropoffTimeZoneRef: null,
  pickupAddressRef: null,
  dropoffAddressRef: null,
  pickupPhoneRef: null,
  dropoffPhoneRef: null,
  pickupNotesRef: null,
  dropoffNotesRef: null,
  willCallRef: null,
  standingOrderRef: null,
  levelOfCareRef: null,
  passengerMobilityRef: null,
  passengerAssistanceRef: null,
  appointmentTimeAllowedForPortalRef: null,
  tripLegEstimatedPickupRefs: [],
  requestOverrideRef: null,
  clientBC: '',
  passengerUUID: '',
  servicePlans: [],
  lastValidationResult: null,
  confidenceByTripIndex: {},
  errorsByTripIndex: {},
  applyTripLegValidationResultsToForm: undefined,
  getPickupTimeZoneForTrip: undefined,
  featureFlags: defaultFeatureFlags,
};

export const reservationFormMainMachine = setup({
  types: {
    context: {} as ReservationFormMainContext,
    events: {} as ReservationFormMainEvent,
    input: {} as ReservationFormMainInput,
  },
  actors: {
    createReservationRequest: fromPromise<
      Reservation,
      {
        clientBC: string;
        passengerUUID: string;
        passengerPhone?: ReservationPhone;
        formValues: ReservationFormValues;
        saveAsDraft: boolean;
        addressValidationEnabled: boolean;
        confidenceByTripIndex: Record<number, TripLegConfidenceStoredEntry>;
      }
    >(async ({ input }) => {
      let { reservation, standingOrder } =
        reservationUtils.getCreateReservationPayloadFromReservationForm(
          input.clientBC,
          input.passengerUUID,
          input.formValues,
          input.passengerPhone,
        );
      if (input.addressValidationEnabled) {
        reservation = mergeTripLegConfidenceResponsesIntoReservationPayload(
          reservation,
          input.confidenceByTripIndex,
        ) as Reservation;
      }
      return firstValueFrom(
        reservationService.createReservation(
          reservation,
          input.formValues.tripType !== TripLegType.MultiLeg
            ? standingOrder
            : undefined,
          input.saveAsDraft,
        ),
      );
    }),
    // Single, sequential validation: /score/trip-leg for every leg first, then
    // /validate only if no blocking trip-leg error surfaced. Both calls resolve
    // before this returns so the result is applied in one transition.
    validateReservation: fromPromise<
      BundledValidationOutput,
      BundledValidationInput
    >(({ input, signal }) => runBundledValidation(input, signal)),
    tripType: tripTypeMachine,
    reasonForTrip: reasonForTripMachine,
    appointmentDate: appointmentDateMachine,
    appointmentDateTime: appointmentDateTimeMachine,
    pickupDate: pickupDateMachine,
    pickupDateTime: pickupDateTimeMachine,
    pickupAddress: pickupAddressMachine,
    dropoffAddress: dropoffAddressMachine,
    pickupPhone: pickupPhoneMachine,
    dropoffPhone: dropoffPhoneMachine,
    pickupNotes: pickupNotesMachine,
    dropoffNotes: dropoffNotesMachine,
    willCall: willCallMachine,
    standingOrder: standingOrderMachine,
    levelOfCare: levelOfCareMachine,
    passengerMobility: passengerMobilityMachine,
    passengerAssistance: passengerAssistanceMachine,
    appointmentTimeAllowedForPortal: appointmentTimeAllowedForPortalMachine,
    tripLegEstimatedPickup: tripLegEstimatedPickupMachine,
    requestOverride: requestOverrideMachine,
  },
  actions: {
    forwardOverrideOpenToDialog: ({
      context,
    }: {
      context: ReservationFormMainContext;
    }) => {
      context.requestOverrideRef?.send({ type: 'OPEN' });
    },
    syncTripLegEstimatedPickupActors: enqueueActions(
      ({ context, event, enqueue }) => {
        if (event.type !== 'SET_TRIP_COUNT') return;
        const count = Math.max(1, event.count);
        if (!context.pickupAddressRef || !context.dropoffAddressRef) return;

        for (
          let i = count;
          i < context.tripLegEstimatedPickupRefs.length;
          i++
        ) {
          enqueue.stopChild(`tripLegEstimatedPickup.${i}`);
        }

        enqueue.assign({
          tripLegEstimatedPickupRefs: ({ context, spawn }) => {
            const next = context.tripLegEstimatedPickupRefs.slice(0, count);
            while (next.length < count) {
              const idx = next.length;
              next.push(
                spawn('tripLegEstimatedPickup', {
                  id: `tripLegEstimatedPickup.${idx}`,
                  input: {
                    pickupAddressRef: context.pickupAddressRef!,
                    dropoffAddressRef: context.dropoffAddressRef!,
                    tripIndex: idx,
                    getDirections: context.getDirections,
                    appointmentDateTimeRef:
                      context.appointmentDateTimeRef ?? undefined,
                    getPickupTimeZone: () =>
                      context.getPickupTimeZoneForTrip?.(idx),
                  },
                }),
              );
            }
            return next;
          },
        });
      },
    ),
    syncTripCount: ({
      context,
      event,
    }: {
      context: ReservationFormMainContext;
      event: ReservationFormMainEvent;
    }) => {
      if (event.type !== 'SET_TRIP_COUNT') return;
      const count = Math.max(1, event.count);
      const refs: (AnyActorRef | null)[] = [
        context.reasonForTripRef,
        context.appointmentDateRef,
        context.appointmentDateTimeRef,
        context.pickupDateRef,
        context.pickupDateTimeRef,
        context.pickupTimeZoneRef,
        context.dropoffTimeZoneRef,
        context.pickupAddressRef,
        context.dropoffAddressRef,
        context.pickupPhoneRef,
        context.dropoffPhoneRef,
        context.pickupNotesRef,
        context.dropoffNotesRef,
        context.willCallRef,
        context.standingOrderRef,
        context.levelOfCareRef,
        context.passengerMobilityRef,
        context.passengerAssistanceRef,
      ];
      refs.forEach((r) => r?.send({ type: 'RESIZE', count }));
    },
    resetAllFields: ({ context }: { context: ReservationFormMainContext }) => {
      context.tripTypeRef?.send({ type: 'RESET' });
      context.reasonForTripRef?.send({ type: 'RESET' });
      const tripFieldRefs: (AnyActorRef | null)[] = [
        context.reasonForTripRef,
        context.appointmentDateRef,
        context.appointmentDateTimeRef,
        context.pickupDateRef,
        context.pickupDateTimeRef,
        context.pickupTimeZoneRef,
        context.dropoffTimeZoneRef,
        context.pickupAddressRef,
        context.dropoffAddressRef,
        context.pickupPhoneRef,
        context.dropoffPhoneRef,
        context.pickupNotesRef,
        context.dropoffNotesRef,
        context.willCallRef,
        context.standingOrderRef,
        context.passengerMobilityRef,
        context.passengerAssistanceRef,
      ];
      tripFieldRefs.forEach((r) => r?.send({ type: 'RESET' }));
      context.levelOfCareRef?.send({ type: 'RESET' });
      context.appointmentTimeAllowedForPortalRef?.send({ type: 'RESET' });
      context.tripLegEstimatedPickupRefs.forEach((r) =>
        r.send({ type: 'RESET' }),
      );
      clearTripLegScoreCache();
    },
    updateFormValuesFromValidationResult: ({ context, event }) => {
      if (!('output' in event)) return;
      const output = event.output as BundledValidationOutput;
      const validations = output?.validations;
      if (validations == null) return;
      // Pass the whole output: the applier surfaces /validate field errors AND
      // /score trip-leg errors (output.errorsByTripIndex) in one pass.
      context.applyTripLegValidationResultsToForm?.()?.(output);
      for (const update of validations.tripLegUpdates) {
        const idx = update.tripLegSequence - 1;
        const trip = context.submitFormValues?.trips[idx];
        if (trip == null) continue;
        if (update.pickUpAddress?.timeZone) {
          trip.pickupTimeZone = update.pickUpAddress.timeZone;
        }
        if (update.dropOffAddress?.timeZone) {
          trip.dropoffTimeZone = update.dropOffAddress.timeZone;
        }
      }
    },
  },
  guards: {
    canSubmit: ({ context }) => canSubmitFromContext(context),
    canSubmitFromSubmitEvent,
    canSaveAsDraftFromEvent,
    canSaveAsDraft: ({ context }) => canSaveAsDraftFromContext(context),
    canReservationBeOverridden: ({ context }) =>
      canReservationBeOverriddenFromContext(context),
    shouldValidateAndHasReservation,
  },
}).createMachine({
  id: 'reservationFormMain',
  context: ({ input }) => ({
    ...initialContext,
    clientBC: input?.clientBC ?? '',
    passengerUUID: input?.passengerUUID ?? '',
    passengerPhone: input?.passengerPhone,
    getDirections: input?.getDirections,
    applyTripLegValidationResultsToForm:
      input?.applyTripLegValidationResultsToForm,
    getPickupTimeZoneForTrip: input?.getPickupTimeZoneForTrip,
    featureFlags: input?.featureFlags ?? defaultFeatureFlags,
  }),
  initial: 'bootstrap',
  states: {
    bootstrap: {
      entry: assign(({ spawn, context }) => {
        const pickupAddressRef = spawn('pickupAddress', {
          id: 'pickupAddress',
        });
        const dropoffAddressRef = spawn('dropoffAddress', {
          id: 'dropoffAddress',
        });
        const tripTypeRef = spawn('tripType', { id: 'tripType' });
        const reasonForTripRef = spawn('reasonForTrip', {
          id: 'reasonForTrip',
        });
        const appointmentDateRef = spawn('appointmentDate', {
          id: 'appointmentDate',
        });
        const appointmentDateTimeRef = spawn('appointmentDateTime', {
          id: 'appointmentDateTime',
        });
        const pickupDateRef = spawn('pickupDate', { id: 'pickupDate' });
        const pickupDateTimeRef = spawn('pickupDateTime', {
          id: 'pickupDateTime',
        });
        const pickupPhoneRef = spawn('pickupPhone', { id: 'pickupPhone' });
        const dropoffPhoneRef = spawn('dropoffPhone', { id: 'dropoffPhone' });
        const pickupNotesRef = spawn('pickupNotes', { id: 'pickupNotes' });
        const dropoffNotesRef = spawn('dropoffNotes', { id: 'dropoffNotes' });
        const willCallRef = spawn('willCall', { id: 'willCall' });
        const standingOrderRef = spawn('standingOrder', {
          id: 'standingOrder',
        });
        const levelOfCareRef = spawn('levelOfCare', { id: 'levelOfCare' });
        const passengerMobilityRef = spawn('passengerMobility', {
          id: 'passengerMobility',
        });
        const passengerAssistanceRef = spawn('passengerAssistance', {
          id: 'passengerAssistance',
        });
        const appointmentTimeAllowedForPortalRef = spawn(
          'appointmentTimeAllowedForPortal',
          {
            id: 'appointmentTimeAllowedForPortal',
            input: { appointmentDateTimeRef },
          },
        );
        const tripLegEstimatedPickupRefs = [
          spawn('tripLegEstimatedPickup', {
            id: 'tripLegEstimatedPickup.0',
            input: {
              pickupAddressRef,
              dropoffAddressRef,
              tripIndex: 0,
              getDirections: context.getDirections,
              appointmentDateTimeRef,
              getPickupTimeZone: () => context.getPickupTimeZoneForTrip?.(0),
            },
          }),
        ];
        const requestOverrideRef = spawn('requestOverride', {
          id: 'requestOverride',
        });
        return {
          tripTypeRef,
          reasonForTripRef,
          appointmentDateRef,
          appointmentDateTimeRef,
          pickupDateRef,
          pickupDateTimeRef,
          pickupAddressRef,
          dropoffAddressRef,
          pickupPhoneRef,
          dropoffPhoneRef,
          pickupNotesRef,
          dropoffNotesRef,
          willCallRef,
          standingOrderRef,
          levelOfCareRef,
          passengerMobilityRef,
          passengerAssistanceRef,
          appointmentTimeAllowedForPortalRef,
          tripLegEstimatedPickupRefs,
          requestOverrideRef,
        };
      }),
      always: { target: 'editing' },
    },
    editing: {
      on: {
        FIELD_EDITED: {
          target: 'validating',
          guard: 'shouldValidateAndHasReservation',
        },
        SUBMIT: {
          target: 'submitting',
          guard: 'canSubmitFromSubmitEvent',
          actions: assign({
            submitFormValues: ({ event }) =>
              event.type === 'SUBMIT' ? event.formValues : undefined,
            saveAsDraft: ({ event }) =>
              event.type === 'SUBMIT' ? false : undefined,
          }),
        },
        SAVE_AS_DRAFT: {
          target: 'submitting',
          guard: 'canSaveAsDraftFromEvent',
          actions: assign({
            submitFormValues: ({ event }) =>
              event.type === 'SAVE_AS_DRAFT' ? event.formValues : undefined,
            saveAsDraft: ({ event }) =>
              event.type === 'SAVE_AS_DRAFT' ? true : undefined,
          }),
        },
        RESET_ALL: {
          actions: [
            'resetAllFields',
            assign({
              tripLegMergedValues: () => undefined,
              confidenceByTripIndex: () => ({}),
              errorsByTripIndex: () => ({}),
            }),
          ],
        },
        OVERRIDE_OPEN: {
          guard: 'canReservationBeOverridden',
          actions: 'forwardOverrideOpenToDialog',
        },
      },
    },
    validating: {
      on: {
        OVERRIDE_OPEN: {
          guard: 'canReservationBeOverridden',
          actions: 'forwardOverrideOpenToDialog',
        },
        FIELD_EDITED: {
          target: 'validating',
          guard: 'shouldValidateAndHasReservation',
          reenter: true,
        },
      },
      invoke: {
        id: 'validateReservation',
        src: 'validateReservation',
        input: ({ context, event }): BundledValidationInput => {
          const addressValidationEnabled =
            context.featureFlags.addressValidationEnabled;
          const isClientPortal = context.featureFlags.isClientPortal;
          if (!isFieldEditedWithReservation(event)) {
            return {
              reservation: null,
              addressValidationEnabled,
              isClientPortal,
            };
          }
          return {
            reservation: event.reservation,
            addressValidationEnabled,
            isClientPortal,
          };
        },
        onDone: {
          target: 'editing',
          actions: [
            'updateFormValuesFromValidationResult',
            assign({
              lastValidationResult: ({ event }) => event.output.validations,
              confidenceByTripIndex: ({ event }) =>
                event.output.confidenceByTripIndex,
              errorsByTripIndex: ({ event }) => event.output.errorsByTripIndex,
            }),
          ],
        },
        onError: { target: 'editing' },
      },
    },
    submitting: {
      invoke: {
        id: 'createReservationRequest',
        src: 'createReservationRequest',
        input: ({ context }) => ({
          clientBC: context.clientBC,
          passengerUUID: context.passengerUUID,
          passengerPhone: context.passengerPhone,
          formValues: context.submitFormValues!,
          saveAsDraft: context.saveAsDraft ?? false,
          addressValidationEnabled:
            context.featureFlags.addressValidationEnabled,
          confidenceByTripIndex: context.confidenceByTripIndex,
        }),
        onDone: { target: 'success' },
        onError: { target: 'editing' },
      },
    },
    success: {
      on: {
        RESET_ALL: {
          target: 'editing',
          actions: [
            'resetAllFields',
            assign({
              lastValidationResult: () => null,
              tripLegMergedValues: () => undefined,
              confidenceByTripIndex: () => ({}),
              errorsByTripIndex: () => ({}),
            }),
          ],
        },
      },
    },
  },
  on: {
    SET_TRIP_COUNT: {
      actions: ['syncTripCount', 'syncTripLegEstimatedPickupActors'],
    },
    SET_PASSENGER: {
      actions: assign({
        clientBC: ({ event }) =>
          event.type === 'SET_PASSENGER' ? event.clientBC : '',
        passengerUUID: ({ event }) =>
          event.type === 'SET_PASSENGER' ? event.passengerUUID : '',
        passengerPhone: ({ event }) =>
          event.type === 'SET_PASSENGER' ? event.passengerPhone : undefined,
      }),
    },
    SET_PORTAL_NAME: {
      actions: ({ context, event }) => {
        if (event.type !== 'SET_PORTAL_NAME') return;
        context.appointmentTimeAllowedForPortalRef?.send({
          type: 'SET_PORTAL_NAME',
          portalName: event.portalName,
        });
      },
    },
    TRIP_LEG_MERGED_VALUES: {
      actions: assign({
        tripLegMergedValues: ({ event }) =>
          event.type === 'TRIP_LEG_MERGED_VALUES'
            ? event.mergedValues
            : undefined,
      }),
    },
  },
});

export type TripLegNextButtonDisabledOptions = {
  isMemberPortal?: boolean;
  hasCareLevelMismatch?: boolean;
};

/*
 * Core logic for determining if the Next button on the Trip Leg form should be disabled.
 */
export function selectTripLegNextButtonDisabled(
  snapshot: SnapshotFrom<typeof reservationFormMainMachine>,
  options?: TripLegNextButtonDisabledOptions,
): boolean {
  const isBusy =
    snapshot.matches('submitting') || snapshot.matches('validating');

  const merged = snapshot.context.tripLegMergedValues;
  if (merged === undefined) {
    return true;
  }

  // Disable the next button if gurney is selected and there is no approval
  const levelOfCareRef = snapshot.context.levelOfCareRef;
  const isGurneySelected =
    levelOfCareRef != null
      ? ((
          levelOfCareRef.getSnapshot().context as LevelOfCareContext
        ).isGurneySelected.some(Boolean) ?? false)
      : false;
  const isOverridden = Boolean(merged.AllowOverride);
  if (isGurneySelected && !isOverridden) {
    return true;
  }

  // Disable the next button if we are in a member portal and there
  // is a care level mismatch
  const isMemberPortal = Boolean(options?.isMemberPortal);
  const hasCareLevelMismatch = Boolean(options?.hasCareLevelMismatch);
  if (isMemberPortal && hasCareLevelMismatch) {
    return true;
  }

  const isSchemaValid = tripLegReservationFormSchema.safeParse(merged).success;
  // Client portal treats low address-confidence (/validate's LOW_CONFIDENCE_ADDRESS)
  // as a non-blocking warning, so exclude it from the errors that gate Next.
  const isClientPortal = snapshot.context.featureFlags.isClientPortal;
  const hasBlockingValidationErrors =
    filterReservationErrorsExcludingMemberPhones(
      snapshot.context.lastValidationResult?.errors ?? [],
    ).filter((e) => !(isClientPortal && isLowConfidenceError(e))).length > 0;
  const isFormOverridden = Boolean(merged.AllowOverride);

  // An outstanding trip-leg (/score/trip-leg) error skips the /validate call, so
  // we can't rely on validate's errors to gate Next — block here instead until
  // it's resolved. Client-portal low address-confidence warnings are not blocking.
  const hasBlockingTripLegErrors = hasBlockingTripLegConfidenceError(
    snapshot.context.errorsByTripIndex,
    snapshot.context.featureFlags.isClientPortal,
  );

  return (
    isBusy ||
    !isSchemaValid ||
    hasBlockingTripLegErrors ||
    (hasBlockingValidationErrors && !isFormOverridden)
  );
}

/*
 * Core logic for determining if the Request Override button should be disabled.
 */
export function selectRequestOverrideButtonDisabled(
  snapshot: SnapshotFrom<typeof reservationFormMainMachine>,
): boolean {
  const isBusy =
    snapshot.matches('submitting') || snapshot.matches('validating');

  const merged = snapshot.context.tripLegMergedValues;
  if (merged === undefined) {
    return true;
  }

  const isFormOverridden = Boolean(merged.AllowOverride);

  return (
    isBusy ||
    isFormOverridden ||
    !canReservationBeOverriddenFromContext(snapshot.context)
  );
}

/*
 * Core logic for determining if the Save As Draft button should be disabled.
 */
export function selectSaveAsDraftButtonDisabled(
  snapshot: SnapshotFrom<typeof reservationFormMainMachine>,
): boolean {
  const isBusy =
    snapshot.matches('submitting') || snapshot.matches('validating');

  const merged = snapshot.context.tripLegMergedValues;
  if (merged === undefined) {
    return true;
  }

  return isBusy || !canSaveAsDraftFromFormValues(merged);
}
